/* Authenticated read-only viewer for a single report from the Report Center. */

import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Loader2, Download, Printer } from "lucide-react";
import ReportRenderer from "../components/ReportRenderer";
import "./ReportHistory.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReportDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, doc: null, error: null });

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/reports/${id}`);
        setState({ loading: false, doc: r.data, error: null });
      } catch (e) {
        setState({ loading: false, doc: null, error: e.response?.data?.detail || "Could not load report." });
      }
    })();
  }, [id]);

  const download = async (format) => {
    try {
      const resp = await axios.get(`${API}/reports/${id}/export?format=${format}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      const title = state.doc?.report?.title || "report";
      const safe = title.replace(/[^\w -]+/g, "_").slice(0, 60);
      a.href = url; a.download = `${safe}.${format}`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) { console.warn("Report export failed:", e); /* user can retry */ }
  };

  if (state.loading) {
    return (
      <div className="rc">
        <div className="rc-loading" data-testid="report-detail-loading">
          <Loader2 size={18} className="rc-spin" /> Loading report…
        </div>
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="rc">
        <div className="rc-empty" data-testid="report-detail-error">
          <h2>{state.error}</h2>
          <button className="rc-empty-cta" onClick={() => navigate("/reports")}>← Back to Report Center</button>
        </div>
      </div>
    );
  }

  const doc = state.doc;
  const isSB = doc.kind === "worship_plan";
  const product = isSB ? "sundaybuilder" : "parish_intelligence";
  const meta = { church: doc.parish_name, title: doc.report?.title };

  return (
    <div className="rc" data-testid="report-detail">
      <header className="rc-top">
        <div className="rc-top-l">
          <button className="rc-back" onClick={() => navigate("/reports")} data-testid="rd-back">
            <ArrowLeft size={13} /> Report Center
          </button>
        </div>
        <div className="rc-actions">
          <button className="rc-new" onClick={() => window.print()} data-testid="rd-print">
            <Printer size={13} /> Print
          </button>
          <button className="rc-new" onClick={() => download("pdf")} data-testid="rd-pdf">
            <Download size={13} /> PDF
          </button>
          <button className="rc-new" onClick={() => download("docx")} data-testid="rd-docx">
            <Download size={13} /> DOCX
          </button>
        </div>
      </header>
      <main className="rc-body" style={{ maxWidth: 900 }}>
        <ReportRenderer report={doc.report} meta={meta} product={product} />
      </main>
    </div>
  );
}
